/**
 * Bridge between the theme and the Fish Wishlist app: everything the app expects from a host theme
 * but cannot get from this one. Three jobs, all of them gap-filling, none of them business logic.
 *
 * 1. Accessible pressed state — `data-is-added` mirrored onto `aria-pressed`.
 * 2. Keyboard activation — `role="button"` on a custom element is operable by nobody.
 * 3. Variant sync on the PDP and in the quick-buy layer.
 * 4. Sending the heart through the theme's size drawer when no size has been chosen yet.
 *
 * On (4): the app ships its own variant picker for that case, which would put a second place to
 * choose a size on the PDP. Décision 6 says the size drawer is *the* place, so the heart follows the
 * add-to-cart CTA through `redirectToSizeDrawerIfUnselected` and the add is replayed once the drawer
 * closes on an actual choice. Arbitrated with Antoine on 2026-08-12, cf.
 * `.docs/wishlist-integration-fish.md` §3.4.
 *
 * On (3): the app refreshes the PDP button by subscribing to Dawn's `PUB_SUB_EVENTS.variantChange`
 * (via a global `subscribe`) or to a jQuery `onVariantChange`. Neither exists here — this theme
 * dispatches its own `variant:selected` — so nothing ever told the button which variant was picked
 * and it kept pointing at the first available one. The theme does not re-render the buy-box on
 * variant change either (price, form and inventory each update themselves in place), so no morph
 * refreshes the attributes as a side effect.
 *
 * The app owns the toggle behaviour and tracks its state through `data-is-added`; nothing in it
 * touches ARIA, so a screen reader would announce the same thing before and after a product is
 * added to the list. Because we author the markup ourselves (manual integration), the pressed
 * state is ours to expose: this module mirrors `data-is-added` onto `aria-pressed`.
 *
 * It also re-syncs when `aria-pressed` alone changes, which is what happens after a morph: the
 * Section Rendering API re-renders `aria-pressed='false'` while `morph.js` preserves the client
 * value of `data-is-added`. Observing both attributes makes the pair self-healing.
 *
 * It also gives these elements keyboard activation. `role="button"` on a custom element is not
 * focusable or operable by itself, and the app binds `click` only — its bundle has no keydown
 * listener at all — so Enter and Space did nothing. Dispatching the click here is therefore safe:
 * there is no second handler to double-toggle. Verified in recette on 2026-08-12.
 */

import { redirectToSizeDrawerIfUnselected } from '@theme/utilities';

const WISHLIST_BUTTON_SELECTOR = 'wishlist-button, wishlist-button-pdp';

/** Every Fish element the theme renders with `role="button"`, including the header trigger. */
const ACTIVATABLE_SELECTOR = `${WISHLIST_BUTTON_SELECTOR}, open-wishlists-button`;

/**
 * Mirrors the app's `data-is-added` state onto `aria-pressed`.
 * @param {Element} element - A wishlist toggle.
 */
function syncPressedState(element) {
  const pressed = element.getAttribute('data-is-added') === 'true' ? 'true' : 'false';

  if (element.getAttribute('aria-pressed') !== pressed) {
    element.setAttribute('aria-pressed', pressed);
  }
}

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    const { target } = mutation;

    if (target instanceof Element && target.matches(WISHLIST_BUTTON_SELECTOR)) {
      syncPressedState(target);
    }
  }
});

// Subtree observation covers nodes inserted later, so injected product cards (Nosto dynamic cards,
// cart confirmation layer, cross-sell) are picked up without a second pass.
observer.observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['data-is-added', 'aria-pressed'],
  subtree: true,
});

// Delegated so injected cards are covered without rebinding. Space is activated on keydown like
// Enter rather than on keyup: the app toggles on a plain click and has no notion of a press cycle,
// so matching the native button's keyup timing would buy nothing.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest(ACTIVATABLE_SELECTOR);
  if (!(button instanceof HTMLElement)) return;

  // Space would otherwise scroll the page.
  event.preventDefault();
  button.click();
});

/**
 * Finds the PDP-style toggle that belongs to the same product as `origin`, by widening the search
 * from the element that changed outwards. Deliberately layout-agnostic: on the PDP the nearest
 * enclosing scope is the buy-box, in the quick-buy layer it is the drawer's product wrapper, and
 * neither has to be named here.
 * @param {Element} origin - The variant picker that fired the event.
 * @returns {Element | null}
 */
function findPdpToggle(origin) {
  /** @type {Element | null} */
  let scope = origin;

  while (scope) {
    const toggle = scope.querySelector('wishlist-button-pdp');
    if (toggle) return toggle;
    scope = scope.parentElement;
  }

  return null;
}

/**
 * Re-targets a PDP toggle at the newly selected variant.
 *
 * Writing the attributes is not enough on its own. `data-variant-id` is observed by the app and
 * refreshes its internal `variantId`, but `preselectedId` is only ever read in its
 * `connectedCallback` — and that is the value its removal path uses, so a stale one un-favourites
 * the wrong variant. Re-inserting the element makes the app re-read all of it through its own public
 * lifecycle, rather than having us assign to its internals. Its click listener is a prototype
 * method, so re-binding it on reconnect is a no-op.
 *
 * @param {Element} toggle - A `wishlist-button-pdp`.
 * @param {string} variantId - The newly selected variant id.
 */
function retargetPdpToggle(toggle, variantId) {
  if (toggle.getAttribute('data-variant-id') === variantId) return;

  toggle.setAttribute('data-variant-id', variantId);
  toggle.setAttribute('data-preselected-id', variantId);

  const parent = toggle.parentNode;
  if (!parent) return;

  const anchor = toggle.nextSibling;
  parent.removeChild(toggle);
  parent.insertBefore(toggle, anchor);
}

/** Hearts whose click is waiting on a size choice. Also stops a double click queueing two adds. */
const awaitingSizeChoice = new WeakSet();

/**
 * Sends a heart that has no chosen variant through the theme's size drawer instead of letting the
 * app open its own variant picker, so the PDP keeps a single place to choose a size (décision 6).
 *
 * Capture phase on `document`: the app binds its handler on the element itself, so stopping
 * propagation here is what keeps it from favouriting anything. Same shape as the add-to-cart guard
 * in `product-buy-box.js`.
 */
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const toggle = target.closest('wishlist-button-pdp');
    if (!toggle || awaitingSizeChoice.has(toggle)) return;

    // A chosen variant means there is nothing to arbitrate: let the app handle the click.
    if (toggle.hasAttribute('data-preselected-id')) return;

    const sizeDrawer = toggle.closest('.shopify-section')?.querySelector('size-drawer');
    if (!redirectToSizeDrawerIfUnselected(toggle) || !sizeDrawer) return;

    event.preventDefault();
    event.stopPropagation();
    awaitingSizeChoice.add(toggle);

    // Closing without choosing must leave the heart untouched — that is the whole point of the
    // guard — so the intent is dropped on close. Fires after the replay in the nominal case, where
    // deleting an already-deleted entry is a no-op.
    sizeDrawer.addEventListener('dialog:close', () => awaitingSizeChoice.delete(toggle), { once: true });
  },
  { capture: true }
);

document.addEventListener('variant:selected', (event) => {
  if (!(event instanceof CustomEvent) || !(event.target instanceof Element)) return;

  // Empty when the chosen combination does not resolve to a variant: leave the button as it is. A
  // non-empty id therefore means a complete choice, which is exactly the signal a deferred add
  // needs — the drawer closing is not, since a drawer holding only one of two option groups never
  // closes on its own.
  const variantId = event.detail?.resource?.id;
  if (!variantId) return;

  const toggle = findPdpToggle(event.target);
  if (!toggle) return;

  retargetPdpToggle(toggle, String(variantId));

  if (!awaitingSizeChoice.has(toggle) || !(toggle instanceof HTMLElement)) return;

  awaitingSizeChoice.delete(toggle);
  toggle.click();
});

/**
 * Fish drawers that take over the screen when they open, with the class they carry while open:
 * `atw-drawer` (choose a list / a variant) and `wishlists-drawer` (the list itself, which the app
 * opens right after an add). Verified in the DOM on the dev store, 2026-08-21.
 */
const APP_DRAWER_SELECTOR = 'atw-drawer, wishlists-drawer';

/**
 * Steps our own layer aside when one of the app's drawers opens underneath it.
 *
 * Their drawers are plain `position: fixed` elements (z-index 10000/10001), so nothing can raise
 * them above a modal `<dialog>`, which lives in the top layer: an add made from the quick-buy layer
 * opened the wishlist drawer *behind* it, where the visitor never saw it (reported in recette on
 * 2026-08-21). Promoting their drawer into the top layer is not ours to do, so the layer covering it
 * closes instead.
 *
 * Watching their drawer rather than the add itself covers both orders: the drawer that confirms an
 * add (after `data-is-added` flips) and the one that asks something first (list choice, variant
 * selector), which no add would ever follow while it stays invisible.
 */
const appDrawerObserver = new MutationObserver((mutations) => {
  for (const { target } of mutations) {
    if (!(target instanceof Element) || !target.matches(APP_DRAWER_SELECTOR)) continue;
    if (!target.classList.contains('active')) continue;

    closeCoveringDialog();
    return;
  }
});

appDrawerObserver.observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['class'],
  subtree: true,
});

/** Closes the open theme dialog, if any: it is the one hiding the app's drawer. */
function closeCoveringDialog() {
  for (const dialog of document.querySelectorAll('dialog[open]')) {
    // Same host lookup as the drawer swap in quick-add.js: a dialog's parent is its component.
    const host = /** @type {any} */ (dialog.parentElement);
    if (typeof host?.closeDialog === 'function') host.closeDialog();
  }
}

for (const element of document.querySelectorAll(WISHLIST_BUTTON_SELECTOR)) {
  syncPressedState(element);
}
